import { useCallback, useEffect, useState } from "react";
import { Baby, Loader2, Printer, RefreshCw, AlertTriangle, LogIn, LogOut, Clock, ImageOff } from "lucide-react";
import { AdminShell } from "../components/AdminShell";
import { PhotoThumb } from "../components/ImageLightbox";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { api, apiError, fileUrl } from "../lib/api";
import { toast } from "sonner";

const ChildPhotoManager = ({ child, onDone }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const { data } = await api.post(`/admin/children/${child.id}/photo`, fd);
      toast.success(data.child_care_eligible
        ? "Fotografía verificada: el niño ya es elegible para cuidado infantil."
        : "Fotografía verificada, pero aún no es elegible (revisa los adultos autorizados).");
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3" data-testid={`photo-manager-${child.id}`}>
      <input type="file" accept="image/jpeg,image/png,image/webp" className="max-w-45 text-xs"
        data-testid={`photo-input-${child.id}`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } }} />
      {preview && <PhotoThumb src={preview} alt="Vista previa de la fotografía" className="h-10 w-10 rounded-lg object-cover" testId={`photo-preview-${child.id}`} />}
      {file && (
        <Button size="sm" onClick={upload} disabled={busy} data-testid={`photo-upload-${child.id}`} className="bg-hz-cyan font-bold text-white">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Validar foto
        </Button>
      )}
    </div>
  );
};

const CareContent = ({ user }) => {
  const isManager = ["admin", "welcome_coordinator"].includes(user?.role);
  const invalidate = async (childId) => {
    if (!window.confirm("¿Invalidar la fotografía? El acceso a cuidado infantil se bloqueará de inmediato.")) return;
    try {
      await api.post(`/admin/children/${childId}/photo/remove`);
      toast.success("Fotografía invalidada. Cuidado infantil bloqueado.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/child-care/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="flex items-center gap-2 py-16 text-hz-navy" data-testid="care-dashboard-loading"><Loader2 className="h-5 w-5 animate-spin" /> Cargando…</div>;
  }
  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="care-dashboard">
      <div className="flex items-center justify-between print:hidden">
        <h2 className="text-lg font-bold text-hz-navy">Panel de cuidado infantil</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="care-refresh-btn" className="font-semibold"><RefreshCw className="mr-1 h-4 w-4" /> Actualizar</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="care-print-btn" className="font-semibold"><Printer className="mr-1 h-4 w-4" /> Imprimir listado</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-6">
        {[
          { icon: Baby, label: "Niños dentro", value: t.inside, testId: "care-total-inside" },
          { icon: LogIn, label: "Entradas hoy", value: t.entries_today, testId: "care-total-entries" },
          { icon: LogOut, label: "Salidas hoy", value: t.exits_today, testId: "care-total-exits" },
          { icon: Clock, label: "Pendientes de recoger", value: t.pending_pickup, testId: "care-total-pending" },
          { icon: AlertTriangle, label: "Alertas de salud", value: t.alerts, testId: "care-total-alerts" },
          { icon: ImageOff, label: "Fotos pendientes", value: t.photo_pending, testId: "care-total-photo-pending" },
        ].map((m) => (
          <div key={m.label} className="rounded-2xl border border-hz-gray/40 bg-white p-4" data-testid={m.testId}>
            <m.icon className="h-5 w-5 text-hz-cyan" />
            <p className="mt-2 text-2xl font-extrabold text-hz-navy">{m.value}</p>
            <p className="text-xs font-semibold text-hz-ink/60">{m.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-hz-gray/40 bg-white" data-testid="care-inside-list">
        <p className="border-b border-hz-gray/40 px-4 py-3 text-sm font-bold text-hz-navy">Niños actualmente dentro ({data.inside.length})</p>
        {data.inside.length === 0 ? (
          <p className="px-4 py-8 text-sm text-hz-ink/60" data-testid="care-inside-empty">No hay niños dentro del área en este momento.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-bold text-hz-navy">Niño</TableHead>
                  <TableHead className="font-bold text-hz-navy">Familia/Grupo</TableHead>
                  <TableHead className="font-bold text-hz-navy">Entrada</TableHead>
                  <TableHead className="font-bold text-hz-navy">Alertas</TableHead>
                  <TableHead className="font-bold text-hz-navy print:hidden">Contacto</TableHead>
                  {isManager && <TableHead className="font-bold text-hz-navy print:hidden">Foto</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.inside.map((c) => (
                  <TableRow key={c.id} data-testid={`care-inside-row-${c.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {c.photo_url && <PhotoThumb src={c.photo_url} alt={`Fotografía de ${c.full_name}`} className="h-10 w-10 rounded-lg object-cover" testId={`care-admin-child-photo-${c.id}`} />}
                        <div>
                          <p className="font-bold text-hz-ink">{c.full_name}</p>
                          {c.age != null && <p className="text-xs text-hz-ink/60">{c.age} años</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{c.group_name || "—"}<br />{c.group_folio}</TableCell>
                    <TableCell className="text-xs">{c.checked_in_at ? new Date(c.checked_in_at).toLocaleString("es-MX") : "—"}</TableCell>
                    <TableCell>
                      {(c.allergies || c.special_needs || c.medical_notes) ? (
                        <span className="rounded-full bg-hz-error/10 px-2 py-1 text-xs font-bold text-hz-error" data-testid={`care-alert-${c.id}`}>
                          {[c.allergies, c.special_needs, c.medical_notes].filter(Boolean).join(" · ")}
                        </span>
                      ) : <span className="text-xs text-hz-ink/50">—</span>}
                    </TableCell>
                    <TableCell className="text-xs print:hidden">{c.primary_contact_name}<br />{c.primary_contact_phone}</TableCell>
                    {isManager && (
                      <TableCell className="print:hidden">
                        <button onClick={() => invalidate(c.id)} data-testid={`care-invalidate-${c.id}`} className="text-xs font-bold text-hz-error hover:underline">
                          Invalidar foto
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {data.photo_pending.length > 0 && (
        <div className="rounded-2xl border border-hz-warning/40 bg-white print:hidden" data-testid="care-photo-pending-list">
          <p className="border-b border-hz-warning/30 px-4 py-3 text-sm font-bold text-hz-warning">
            <ImageOff className="mr-1.5 inline h-4 w-4" /> Fotografías pendientes ({data.photo_pending.length})
          </p>
          <ul className="divide-y divide-hz-gray/30">
            {data.photo_pending.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`photo-pending-row-${c.id}`}>
                <div>
                  <p className="text-sm font-bold text-hz-ink">{c.full_name} <span className="text-xs font-medium text-hz-ink/60">· {c.group_name || c.group_folio}</span></p>
                  <p className="text-xs font-semibold text-hz-warning">Fotografía pendiente: acceso a cuidado infantil bloqueado.</p>
                </div>
                {isManager && <ChildPhotoManager child={c} onDone={load} />}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-2xl border border-hz-gray/40 bg-white print:hidden" data-testid="care-open-incidents">
        <p className="border-b border-hz-gray/40 px-4 py-3 text-sm font-bold text-hz-navy">Incidencias abiertas de cuidado infantil ({data.open_incidents.length})</p>
        {data.open_incidents.length === 0 ? (
          <p className="px-4 py-6 text-sm text-hz-ink/60">Sin incidencias abiertas.</p>
        ) : (
          <ul className="divide-y divide-hz-gray/30">
            {data.open_incidents.map((i) => (
              <li key={i.id} className="px-4 py-3 text-sm" data-testid={`care-incident-${i.id}`}>
                <span className={`mr-2 rounded-full px-2 py-0.5 text-xs font-bold ${i.priority === "high" || i.priority === "critical" ? "bg-hz-error/10 text-hz-error" : "bg-hz-warning/10 text-hz-warning"}`}>{i.priority}</span>
                <span className="text-hz-ink">{i.description}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const AdminChildCare = () => (
  <AdminShell roles={["admin", "welcome_coordinator", "child_care_staff"]} title="Cuidado infantil" testId="admin-childcare"
    subtitle="Supervisión del área de cuidado infantil: niños dentro, movimientos del día e incidencias.">
    {(user) => <CareContent user={user} />}
  </AdminShell>
);

export default AdminChildCare;
